import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth, startGoogleLogin } from "../lib/auth";
import { pushSupport, currentSubscription, subscribePush, unsubscribePush, sendPushTest } from "../lib/push";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { browserTimezone } from "../lib/timezone";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

export default function SettingsDialog({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState({ name: "", reminder_email: "", reminders_enabled: true, wall_opt_in: true });
  const [billing, setBilling] = useState(null);
  const [referralCount, setReferralCount] = useState(0);
  const [emailStats, setEmailStats] = useState(null);
  const [pushState, setPushState] = useState({ enabled: false, busy: false });
  const [pushCaps] = useState(() => pushSupport());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/me"),
      api.get("/billing/me").catch(() => ({ data: null })),
      api.get("/billing/referrals/my").catch(() => ({ data: [] })),
      api.get("/email/stats").catch(() => ({ data: null })),
      currentSubscription().catch(() => null),
    ])
      .then(([s, b, r, es, sub]) => {
        setSettings({
          name: s.data.name || "",
          reminder_email: s.data.reminder_email || "",
          reminders_enabled: s.data.reminders_enabled !== false,
          wall_opt_in: s.data.wall_opt_in !== false,
        });
        setBilling(b.data);
        setReferralCount((r.data || []).length);
        setEmailStats(es.data);
        setPushState({ enabled: !!sub, busy: false });
      })
      .finally(() => setLoading(false));
  }, [open]);

  const togglePush = async (next) => {
    setPushState((p) => ({ ...p, busy: true }));
    try {
      if (next) {
        await subscribePush();
        toast.success("Push notifications enabled");
        setPushState({ enabled: true, busy: false });
      } else {
        await unsubscribePush();
        toast.success("Push notifications disabled");
        setPushState({ enabled: false, busy: false });
      }
    } catch (e) {
      toast.error(e.message || "Could not toggle push notifications");
      setPushState((p) => ({ ...p, busy: false }));
    }
  };

  const testPush = async () => {
    try {
      const r = await sendPushTest();
      if (r.sent > 0) toast.success(`Test push sent to ${r.sent} device${r.sent === 1 ? "" : "s"}`);
      else toast.error("No active push devices");
    } catch (e) {
      toast.error("Could not send test push");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me", {
        name: settings.name || null,
        reminder_email: settings.reminder_email || null,
        reminders_enabled: settings.reminders_enabled,
        wall_opt_in: settings.wall_opt_in,
      });
      toast.success("Settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!settings.reminder_email) {
      toast.error("Set a reminder email first");
      return;
    }
    setTesting(true);
    try {
      await api.patch("/me", { reminder_email: settings.reminder_email });
      await api.post("/me/test-reminder");
      toast.success("Test email sent — check your inbox");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test email");
    } finally {
      setTesting(false);
    }
  };

  const sendWeeklyDigestPreview = async () => {
    if (!settings.reminder_email) {
      toast.error("Set a reminder email first");
      return;
    }
    setTesting(true);
    try {
      await api.patch("/me", { reminder_email: settings.reminder_email });
      const r = await api.post("/digest/weekly/send-now");
      const s = r.data.stats;
      const patternMsg = r.data.pattern
        ? `Sent · pattern: "${r.data.pattern.length > 90 ? r.data.pattern.slice(0, 87) + '…' : r.data.pattern}"`
        : `Weekly digest sent — ${s.sessions_completed} sessions, ${s.tasks_completed_count} tasks, ${s.goals_closed} goals.`;
      toast.success(patternMsg);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || "Could not send weekly digest");
    } finally {
      setTesting(false);
    }
  };

  // ---- Founder access ----
  const [founderCode, setFounderCode] = useState("");
  const [founderBusy, setFounderBusy] = useState(false);
  const isFounder = billing?.billing_cycle === "founder";

  const redeemFounder = async () => {
    if (!founderCode.trim()) {
      toast.error("Enter your founder code");
      return;
    }
    setFounderBusy(true);
    try {
      const r = await api.post("/founder/redeem", { code: founderCode.trim() });
      toast.success("Founder access unlocked — welcome to the boardroom.");
      setFounderCode("");
      // Refresh billing state so the UI shows the new plan
      const b = await api.get("/billing/me").catch(() => ({ data: null }));
      setBilling(b.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not redeem code";
      toast.error(detail);
    } finally {
      setFounderBusy(false);
    }
  };

  // ---- Recover a lost session (account merge) ----
  const [lostId, setLostId] = useState("");
  const [merging, setMerging] = useState(false);

  const mergeLostAccount = async () => {
    const lost = lostId.trim();
    if (!lost) {
      toast.error("Enter your old device's user id.");
      return;
    }
    setMerging(true);
    try {
      const r = await api.post("/me/merge", { lost_user_id: lost });
      const d = r.data || {};
      const summary = [
        d.meetings ? `${d.meetings} session${d.meetings === 1 ? "" : "s"}` : null,
        d.action_items ? `${d.action_items} action item${d.action_items === 1 ? "" : "s"}` : null,
        d.goals ? `${d.goals} goal${d.goals === 1 ? "" : "s"}` : null,
      ].filter(Boolean).join(" · ");
      toast.success(summary ? `Recovered · ${summary}` : "Recovered — nothing to move.");
      setLostId("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || "Could not recover that session.");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#fafafa] max-h-[90vh] flex flex-col p-0 gap-0"
        data-testid="settings-dialog"
      >
        <DialogHeader className="p-6 pb-4 border-b border-[#1a1a1a] shrink-0">
          <DialogTitle className="font-serif text-2xl">Boardroom settings</DialogTitle>
          <DialogDescription className="text-[#888]">
            Email reminders fire 15 minutes before each scheduled session.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="settings-body-scroll">
        {loading ? (
          <p className="text-sm text-[#666]">Loading…</p>
        ) : (
          <div className="space-y-5">
            {billing?.effective?.tier && (
              <div className="flex items-center justify-between p-4 rounded-md bg-[#121212] border border-[#2a2a2a]" data-testid="settings-plan-row">
                <div>
                  <p className="label-eyebrow">Current plan</p>
                  <p className="font-serif text-lg mt-1 capitalize">
                    {billing.effective.tier}
                    {billing.effective.source === "trial" && <span className="ml-2 text-xs font-mono text-[#c89f45]">TRIAL</span>}
                  </p>
                  {billing.effective.expires_at && (
                    <p className="text-[10px] font-mono text-[#666] mt-1">
                      {billing.effective.source === "trial" ? "TRIAL ENDS" : "RENEWS"} {new Date(billing.effective.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <Link to="/pricing" onClick={() => onOpenChange(false)}>
                  <Button variant="outline" className="bg-transparent border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a]" data-testid="settings-manage-plan">
                    {billing.effective.tier === "free" ? "Upgrade" : "Manage"}
                  </Button>
                </Link>
              </div>
            )}
            {referralCount > 0 && (
              <Link
                to="/referrals"
                onClick={() => onOpenChange(false)}
                className="flex items-center justify-between p-4 rounded-md bg-[#121212] border border-[#c89f45]/30 hover:border-[#c89f45] transition-colors"
                data-testid="settings-referrals-link"
              >
                <div>
                  <p className="label-eyebrow text-[#c89f45]">Your referral codes</p>
                  <p className="text-sm mt-1 text-[#aaa]">
                    {referralCount} personal code{referralCount === 1 ? "" : "s"} · 30% off for someone you invite
                  </p>
                </div>
                <span className="text-xs font-mono text-[#c89f45]">VIEW →</span>
              </Link>
            )}
            <div>
              <Label className="label-eyebrow">Display name</Label>
              <Input
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                placeholder="Chair"
                className="mt-2 h-11 sm:h-9 bg-[#121212] border-[#2a2a2a] focus:border-[#c89f45]"
                data-testid="settings-name-input"
              />
              <p className="text-xs text-[#8a8a8a] mt-1">Used on action-item sign-off badges.</p>
            </div>
            <div>
              <Label className="label-eyebrow">Reminder email</Label>
              <Input
                type="email"
                value={settings.reminder_email}
                onChange={(e) => setSettings({ ...settings, reminder_email: e.target.value })}
                placeholder="you@example.com"
                className="mt-2 h-11 sm:h-9 bg-[#121212] border-[#2a2a2a] focus:border-[#c89f45]"
                data-testid="settings-email-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="label-eyebrow">Reminders</Label>
                <p className="text-xs text-[#8a8a8a] mt-1">Master switch for email reminders.</p>
              </div>
              <Switch
                checked={settings.reminders_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, reminders_enabled: v })}
                className="data-[state=checked]:bg-[#c89f45]"
                data-testid="settings-reminders-toggle"
              />
            </div>
            <div className="flex items-start justify-between gap-4" data-testid="settings-timezone-row">
              <div>
                <Label className="label-eyebrow">Your timezone</Label>
                <p className="text-xs text-[#8a8a8a] mt-1">
                  Nudges land at 8am your time. Detected from this device.
                </p>
              </div>
              <span className="text-xs font-mono text-[#c89f45] text-right shrink-0" data-testid="settings-timezone-value">
                {settings.timezone || browserTimezone()}
              </span>
            </div>
            {pushCaps.supported && (
              <div className="flex items-center justify-between" data-testid="settings-push-row">
                <div className="flex-1 pr-4">
                  <Label className="label-eyebrow">Push notifications</Label>
                  <p className="text-xs text-[#8a8a8a] mt-1">
                    {pushCaps.isIOS && !pushCaps.isStandalone
                      ? "iOS: Add Boardroom to your Home Screen first, then open it from there."
                      : "Native OS notifications for session reminders, Friday nudge, and Sunday digest."}
                  </p>
                </div>
                <Switch
                  checked={pushState.enabled}
                  onCheckedChange={togglePush}
                  disabled={pushState.busy || (pushCaps.isIOS && !pushCaps.isStandalone)}
                  className="data-[state=checked]:bg-[#c89f45]"
                  data-testid="settings-push-toggle"
                />
              </div>
            )}
            {pushState.enabled && (
              <Button
                onClick={testPush}
                variant="outline"
                className="bg-transparent border-[#2a2a2a] text-[#aaa] hover:bg-[#1a1a1a] hover:text-white rounded-md w-fit"
                data-testid="settings-send-test-push"
              >
                Send test push
              </Button>
            )}
            <div className="flex items-center justify-between">
              <div>
                <Label className="label-eyebrow">Show me on the Founders wall</Label>
                <p className="text-xs text-[#8a8a8a] mt-1">Public page at /founders. Off = your name is hidden.</p>
              </div>
              <Switch
                checked={settings.wall_opt_in}
                onCheckedChange={(v) => setSettings({ ...settings, wall_opt_in: v })}
                className="data-[state=checked]:bg-[#c89f45]"
                data-testid="settings-wall-toggle"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={sendTest}
                disabled={testing}
                variant="outline"
                className="bg-transparent border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a] rounded-md w-fit"
                data-testid="settings-send-test"
              >
                {testing ? "Sending…" : "Send test email"}
              </Button>
              <Button
                onClick={sendWeeklyDigestPreview}
                disabled={testing}
                variant="outline"
                className="bg-transparent border-[#2a2a2a] text-[#ddd] hover:bg-[#c89f45]/10 hover:text-[#c89f45] hover:border-[#c89f45] rounded-md w-fit"
                data-testid="settings-send-weekly-digest"
              >
                Preview weekly digest
              </Button>
            </div>
            {emailStats && emailStats.by_message && emailStats.by_message.length > 0 && (
              <div className="mt-2 border-t border-[#1a1a1a] pt-4" data-testid="settings-email-stats">
                <Label className="label-eyebrow">Email opens (last 90 days)</Label>
                <p className="text-xs text-[#666] mt-1 mb-3">{emailStats.total_opens} total open{emailStats.total_opens === 1 ? "" : "s"} across all Boardroom emails.</p>
                <div className="space-y-2">
                  {emailStats.by_message.map((row) => {
                    const label = {
                      cadence_nudge: "Friday cadence nudges",
                      sunday_digest: "Sunday digests",
                      partner_minutes: "Partner minutes",
                      reminder: "Session reminders",
                    }[row.msg] || row.msg;
                    return (
                      <div key={row.msg} className="flex items-center justify-between text-sm" data-testid={`stats-row-${row.msg}`}>
                        <span className="text-[#ddd]">{label}</span>
                        <span className="text-[#c89f45] font-mono text-xs">{row.opens} open{row.opens === 1 ? "" : "s"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Security — change password (email/password accounts only) */}
            <ChangePasswordSection user={user} />

            {/* Founder access */}
            <div className="mt-2 border-t border-[#1a1a1a] pt-4" data-testid="settings-founder-section">
              <Label className="label-eyebrow">Founder access</Label>
              {isFounder ? (
                <p className="text-sm text-[#c89f45] mt-2" data-testid="founder-status-active">
                  ✓ Lifetime Syndicate — welcome to the boardroom.
                </p>
              ) : (
                <>
                  <p className="text-xs text-[#666] mt-1 mb-3">
                    Enter your founder code to unlock lifetime access to every plan feature.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      value={founderCode}
                      onChange={(e) => setFounderCode(e.target.value)}
                      placeholder="Founder code"
                      className="h-11 sm:h-9 bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] rounded-md flex-1"
                      data-testid="founder-code-input"
                    />
                    <Button
                      onClick={redeemFounder}
                      disabled={founderBusy}
                      className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md"
                      data-testid="founder-redeem-btn"
                    >
                      {founderBusy ? "Verifying…" : "Redeem"}
                    </Button>
                  </div>
                </>
              )}
            </div>
            {/* Recover a lost session (self-service account merge) */}
            <div className="mt-2 border-t border-[#1a1a1a] pt-4" data-testid="settings-merge-section">
              <Label className="label-eyebrow">Recover a lost session</Label>
              {user ? (
                <>
                  <p className="text-xs text-[#666] mt-1 mb-3">
                    Recorded a session on another device before signing in?
                    Paste that device's user id here — every session, action
                    item, and goal that lived under it will move to this
                    account. Signed-in accounts can't be absorbed, so this
                    only works for anonymous devices.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={lostId}
                      onChange={(e) => setLostId(e.target.value)}
                      placeholder="Old device user id"
                      className="h-11 sm:h-9 bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] rounded-md flex-1 font-mono"
                      data-testid="merge-lost-id-input"
                    />
                    <Button
                      onClick={mergeLostAccount}
                      disabled={merging || !lostId.trim()}
                      className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="merge-lost-btn"
                    >
                      {merging ? "Recovering…" : "Recover"}
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#4a4a4a] mt-3 font-mono">
                    Tip · your current user id is on file. You can find an old
                    device's id under <code className="text-[#666]">Application →
                    Local Storage → boardroom_user_id</code> in browser devtools.
                  </p>
                </>
              ) : (
                <p className="text-xs text-[#666] mt-2">
                  Sign in with Google first to recover a lost session.
                </p>
              )}
            </div>
          </div>
        )}
        </div>
        <DialogFooter className="p-6 pt-4 border-t border-[#1a1a1a] shrink-0">
          {user ? (
            <Button
              variant="outline"
              onClick={async () => {
                await logout();
                toast.success("Signed out");
                onOpenChange(false);
              }}
              className="h-11 sm:h-9 bg-transparent border-[#2a2a2a] text-[#aaa] hover:bg-[#1a1a1a] hover:text-white mr-auto"
              data-testid="settings-sign-out"
            >
              Sign out
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={startGoogleLogin}
              className="bg-transparent border-[#2a2a2a] text-[#c89f45] hover:bg-[#1a1a1a] hover:text-[#c89f45] hover:border-[#c89f45] mr-auto"
              data-testid="settings-sign-in"
            >
              Sign in with Google
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 sm:h-9 bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white" data-testid="settings-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="h-11 sm:h-9 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a]" data-testid="settings-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Change-password form. Rendered inside SettingsDialog. Hides itself entirely
 * for signed-out users. If the backend replies with `no_password_set` (Google-
 * only account), we swap to a helpful "use Forgot password" hint rather than
 * a scary error toast.
 */
function ChangePasswordSection({ user }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);

  if (!user) return null;

  const submit = async () => {
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed. Other devices have been signed out.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = typeof d === "object" ? d?.code : null;
      if (code === "no_password_set") {
        setNeedsPassword(true);
      } else if (code === "wrong_current_password") {
        toast.error("Current password is incorrect.");
      } else if (code === "same_password") {
        toast.error("Pick a password different from your current one.");
      } else if (code === "weak_password") {
        toast.error("Password must be at least 8 characters.");
      } else {
        toast.error((typeof d === "string" ? d : d?.message) || "Could not change password.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 border-t border-[#1a1a1a] pt-4" data-testid="settings-security-section">
      <Label className="label-eyebrow">Security · Change password</Label>
      {needsPassword ? (
        <p className="text-xs text-[#c89f45] mt-2" data-testid="change-password-google-hint">
          This account signs in with Google. Sign out, then use "Forgot password"
          from the sign-in dialog to set your first password (we'll email a code).
        </p>
      ) : (
        <>
          <p className="text-xs text-[#666] mt-1 mb-3">
            Rotating the password signs out every other device instantly.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="Current password"
              className="h-11 sm:h-9 bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] rounded-md sm:col-span-2"
              autoComplete="current-password"
              data-testid="change-password-current"
            />
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="New password (8+ chars)"
              className="h-11 sm:h-9 bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] rounded-md"
              autoComplete="new-password"
              data-testid="change-password-new"
            />
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="h-11 sm:h-9 bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] rounded-md"
              autoComplete="new-password"
              data-testid="change-password-confirm"
            />
          </div>
          <Button
            onClick={submit}
            disabled={busy || !current || !next || !confirm}
            className="mt-3 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="change-password-submit"
          >
            {busy ? "Rotating…" : "Change password"}
          </Button>
        </>
      )}
    </div>
  );
}
